import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, EmptyState, StatusBadge } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Archive, Plus, RotateCcw, ShieldCheck, UsersRound } from "lucide-react";

const REGIONS = ["Global", "US", "CA", "GCC", "IN"];
const EMPTY = { name: "", region: "Global", primary_admin_id: "", member_ids: [], status: "active" };

export default function RATeams() {
  const [teams, setTeams] = useState([]);
  const [options, setOptions] = useState({ admins: [], members: [] });
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [teamsRes, optRes] = await Promise.all([
        api.get("/ra-teams", { params: { status: statusFilter } }),
        api.get("/ra-teams/options"),
      ]);
      setTeams(teamsRes.data.items);
      setOptions(optRes.data);
    } catch (error) {
      toast.error(apiErr(error));
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openDialog = (team = null) => {
    setEditing(team);
    setForm(team ? {
      name: team.name,
      region: team.region || "Global",
      primary_admin_id: team.primary_admin_id || "",
      member_ids: (team.members || []).map((m) => m.id),
      status: team.status || "active",
    } : EMPTY);
    setDialog(true);
  };

  const toggleMember = (id) => {
    setForm((f) => ({
      ...f,
      member_ids: f.member_ids.includes(id) ? f.member_ids.filter((m) => m !== id) : [...f.member_ids, id],
    }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("A team name is required");
    if (!form.primary_admin_id) return toast.error("Select a Team Admin");
    setSaving(true);
    try {
      if (editing) await api.put(`/ra-teams/${editing.id}`, form);
      else await api.post("/ra-teams", form);
      toast.success(editing ? "Team updated" : "Team created");
      setDialog(false);
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (team) => {
    const action = team.status === "active" ? "archive" : "restore";
    try {
      await api.post(`/ra-teams/${team.id}/${action}`);
      toast.success(action === "archive" ? "Team archived" : "Team restored");
      load();
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  return (
    <div className="space-y-5" data-testid="ra-teams-page">
      <PageHeader
        title="R.A. Teams"
        subtitle="Create research teams and assign a Team Admin + members. Admins see only their own team's data; Super Admin sees everything."
        actions={
          <Button onClick={() => openDialog()} data-testid="ra-team-create-button" className="bg-[#255058] text-white hover:bg-[#1c3d43]">
            <Plus className="mr-1.5 h-4 w-4" /> New Team
          </Button>
        }
      />

      <div className="grid gap-3 sm:grid-cols-3" data-testid="ra-teams-metrics">
        <Metric icon={UsersRound} label="Teams" value={teams.length} />
        <Metric icon={ShieldCheck} label="Available Team Admins" value={options.admins.length} />
        <Metric icon={UsersRound} label="Assignable Analysts" value={options.members.length} />
      </div>

      <Card className="p-3">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Show</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48" data-testid="ra-teams-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All teams</SelectItem>
              <SelectItem value="active">Active teams</SelectItem>
              <SelectItem value="archived">Archived teams</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="overflow-hidden" data-testid="ra-teams-table">
        {!teams.length ? (
          <EmptyState icon={UsersRound} title="No teams yet" description="Create your first R.A. team to organise research analysts." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Team</TableHead>
                <TableHead>Team Admin</TableHead>
                <TableHead className="hidden lg:table-cell">Region</TableHead>
                <TableHead>Members</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teams.map((team) => (
                <TableRow key={team.id} data-testid={`ra-team-row-${team.id}`}>
                  <TableCell>
                    <div className="font-medium">{team.name}</div>
                    <div className="text-xs text-muted-foreground">{team.code}</div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{team.primary_admin_name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{team.primary_admin_role || ""}</div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">{team.region || "Global"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {(team.members || []).slice(0, 4).map((m) => (
                        <Badge key={m.id} variant="secondary" className="text-[10px]">{m.name}</Badge>
                      ))}
                      {team.member_count > 4 && <Badge variant="outline" className="text-[10px]">+{team.member_count - 4}</Badge>}
                      {!team.member_count && <span className="text-xs text-muted-foreground">No members</span>}
                    </div>
                  </TableCell>
                  <TableCell><StatusBadge value={team.status} /></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openDialog(team)} data-testid={`ra-team-edit-${team.id}`}>Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => changeStatus(team)} data-testid={`ra-team-toggle-${team.id}`}>
                        {team.status === "active" ? <Archive className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Team" : "New R.A. Team"}</DialogTitle>
            <DialogDescription>Assign one Team Admin and the analysts who belong to this team.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="ra-team-name">Team name</Label>
              <Input id="ra-team-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ra-team-name" placeholder="e.g. Fintech Research Desk" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Region</Label>
                <Select value={form.region} onValueChange={(region) => setForm({ ...form, region })}>
                  <SelectTrigger data-testid="ra-team-region"><SelectValue /></SelectTrigger>
                  <SelectContent>{REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {editing && (
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(status) => setForm({ ...form, status })}>
                    <SelectTrigger data-testid="ra-team-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div>
              <Label>Team Admin</Label>
              <Select value={form.primary_admin_id} onValueChange={(primary_admin_id) => setForm({ ...form, primary_admin_id })}>
                <SelectTrigger data-testid="ra-team-admin"><SelectValue placeholder="Select a manager, VP or R.A. admin" /></SelectTrigger>
                <SelectContent>
                  {options.admins.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name} · {a.role_label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Members (Research Analysts)</Label>
              <div className="mt-1 max-h-52 space-y-1 overflow-y-auto rounded-md border border-border p-2" data-testid="ra-team-members">
                {options.members.map((m) => (
                  <label key={m.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent/50">
                    <Checkbox checked={form.member_ids.includes(m.id)} onCheckedChange={() => toggleMember(m.id)} data-testid={`ra-team-member-${m.id}`} />
                    <span className="flex-1">{m.name}</span>
                    <span className="text-xs text-muted-foreground">{m.email}</span>
                  </label>
                ))}
                {!options.members.length && <div className="px-2 py-3 text-sm text-muted-foreground">No research analysts available.</div>}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{form.member_ids.length} selected</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="ra-team-save" className="bg-[#255058] text-white hover:bg-[#1c3d43]">
              {saving ? "Saving…" : editing ? "Save Changes" : "Create Team"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-[#255058]" />
        <div>
          <div className="font-display text-2xl font-bold">{value}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </div>
      </div>
    </Card>
  );
}
